import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { CopyIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ReloadIcon } from '@radix-ui/react-icons'
import { refactor } from '@/utils/requests'
import { useToast } from '@/components/ui/use-toast'

const useRefactor = ({ originalText }: { originalText: string }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [modifiedText, setModifiedText] = useState<string | null>(null)
  const { toast } = useToast()
  const doRefactor = async () => {
    setIsLoading(true)
    try {
      const res = await refactor(originalText)
      setModifiedText(res)
    } catch {
      toast({
        title: 'Something went wrong!',
        description: 'Failed to paraphrase. Please try again in a few minutes',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    doRefactor,
    modifiedText,
  }
}

export const Playground = () => {
  const [inputText, setInputText] = useState('')
  const { modifiedText, isLoading, doRefactor } = useRefactor({
    originalText: inputText,
  })
  const defaultValue = useMemo(() => {
    return isLoading
      ? 'Please wait while we paraphrase to avoid detection. Sometimes it takes up to two minutes for our advanced AI to run'
      : ''
  }, [isLoading])
  const { toast } = useToast()
  return (
    <div className="bg-card/80 shadow-2xl rounded-3xl p-10 flex flex-col gap-8 border border-border backdrop-blur-md transition-all duration-300 hover:shadow-[0_8px_40px_rgba(0,0,0,0.10)] animate-fadeInUp">
      <p className="text-muted-foreground text-center mb-4 text-base">Paste your code or text, and let our AI refactor it for you. Fast, private, and undetectable.</p>
      <div className="flex flex-col md:flex-row gap-10">
        <div className="flex-1 flex flex-col gap-2">
          <label className="font-semibold mb-1" htmlFor="input-text">Input Text</label>
          <Textarea
            id="input-text"
            value={inputText}
            onChange={(event) => setInputText(event.target.value)}
            disabled={isLoading}
            className="resize-none h-56 md:h-[40vh] bg-background/80 border border-border rounded-xl shadow focus:ring-2 focus:ring-primary transition-all"
            placeholder="Paste or type your code/text here..."
          />
          <div className="flex items-center justify-between mt-3 gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                const text = await navigator.clipboard.readText()
                setInputText(text)
                toast({ title: 'Pasted from clipboard!' })
              }}
              className="gap-2"
            >
              <CopyIcon className="h-4 w-4" /> Paste
            </Button>
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={doRefactor}
              disabled={isLoading || !inputText.trim()}
              className="gap-2 font-semibold shadow-md disabled:bg-muted disabled:text-muted-foreground"
            >
              {isLoading ? (
                <><ReloadIcon className="mr-2 h-4 w-4 animate-spin" />Refactoring...</>
              ) : (
                <><ReloadIcon className="h-4 w-4" /> Refactor</>
              )}
            </Button>
          </div>
        </div>
        <div className="hidden md:flex items-center mx-4">
          <div className="h-[40vh] w-1 bg-gradient-to-b from-transparent via-primary/30 to-transparent rounded-full" />
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <label className="font-semibold mb-1" htmlFor="output-text">Refactored Output</label>
          <Textarea
            id="output-text"
            disabled={isLoading}
            className="resize-none h-56 md:h-[40vh] bg-muted/60 border border-border rounded-xl shadow"
            readOnly
            value={modifiedText ?? defaultValue}
            placeholder="Your refactored text will appear here..."
          />
          <div className="flex items-center justify-between mt-3 gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(modifiedText ?? '')
                toast({ title: 'Copied to clipboard!' })
              }}
              disabled={!modifiedText}
              className="gap-2 disabled:bg-muted disabled:text-muted-foreground"
            >
              <CopyIcon className="h-4 w-4" /> Copy Output
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={doRefactor}
              disabled={isLoading || !inputText.trim()}
              className="gap-2"
            >
              <ReloadIcon className="h-4 w-4" /> Reload
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
